import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import i18n from "../../i18n";
import type { ArchiveReason, ArchiveSource } from "./jobs";

const ALL_SOURCES: ArchiveSource[] = ["positions", "events", "commands", "geofences"];

const t = (key: string) => i18n.t(key, { ns: "respaldos" }) as string;

function isDark(): boolean {
  const stored = localStorage.getItem("app-theme-preference");
  if (stored === "dark") return true;
  if (stored === "light") return false;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? true;
}

export type BackupChoice =
  | { action: "archive"; sources: ArchiveSource[] }
  | { action: "skip" }
  | { action: "cancel" };

function BackupSourcesModal({
  reason,
  dark,
  v5Note,
  entity,
  side,
  resolve,
}: {
  reason: ArchiveReason;
  dark: boolean;
  v5Note: boolean;
  entity: "company" | "device";
  side: "sg" | "v5";
  resolve: (c: BackupChoice) => void;
}) {
  const [sel, setSel] = useState<Record<ArchiveSource, boolean>>({
    positions: true,
    events: true,
    commands: true,
    geofences: true,
  });
  const selected = ALL_SOURCES.filter((s) => sel[s]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") resolve({ action: "cancel" });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [resolve]);

  const card = dark
    ? "bg-dark-surface text-white border border-dark-border"
    : "bg-white text-gray-900 border border-gray-200";
  const muted = dark ? "text-gray-300" : "text-gray-500";
  const rowBg = dark ? "hover:bg-white/5" : "hover:bg-gray-50";

  const isSuspend = reason === "suspend";
  const isDevice = entity === "device";
  const confirmLabel = isSuspend ? t("pick.confirmSuspend") : t("pick.confirmDelete");
  const skipLabel = isSuspend ? t("pick.skipSuspend") : t("pick.skipDelete");
  const title = isSuspend
    ? t("pick.titleSuspend")
    : isDevice
      ? t("pick.titleDeleteDevice")
      : t("pick.titleDelete");
  const text = isSuspend
    ? t("pick.textSuspend")
    : isDevice
      ? t("pick.textDeleteDevice")
      : t("pick.textDelete");

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4">
      <div className={`w-full max-w-md rounded-2xl p-6 shadow-xl ${card}`}>
        <h2 className="text-lg font-bold">{title}</h2>
        <p className={`mt-2 text-sm ${muted}`}>{text}</p>

        {(v5Note || isDevice) && (
          <div
            className={`mt-3 rounded-lg border px-3 py-2 text-xs ${
              dark
                ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
                : "border-amber-300 bg-amber-50 text-amber-800"
            }`}
          >
            {isDevice
              ? t("pick.deviceCascadeNote")
              : isSuspend
                ? t(side === "v5" ? "pick.sgNoteSuspend" : "pick.v5NoteSuspend")
                : t(side === "v5" ? "pick.sgNoteDelete" : "pick.v5NoteDelete")}
          </div>
        )}

        <div className="mt-4 space-y-1">
          {ALL_SOURCES.map((s) => (
            <label
              key={s}
              className={`flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 ${rowBg}`}
            >
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary-500"
                checked={sel[s]}
                onChange={(e) => setSel((p) => ({ ...p, [s]: e.target.checked }))}
              />
              <span className="text-sm">{t(`sources.${s}`)}</span>
            </label>
          ))}
        </div>

        <p className={`mt-3 text-xs ${muted}`}>{t("pick.background")}</p>

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => resolve({ action: "cancel" })}
            className={`rounded-lg px-3 py-2 text-sm font-medium ${
              dark ? "text-gray-300 hover:bg-white/5" : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            {t("pick.cancel")}
          </button>
          <button
            type="button"
            onClick={() => resolve({ action: "skip" })}
            className={`rounded-lg px-3 py-2 text-sm font-medium border ${
              dark ? "border-dark-border hover:bg-white/5" : "border-gray-200 hover:bg-gray-50"
            }`}
          >
            {skipLabel}
          </button>
          <button
            type="button"
            disabled={selected.length === 0}
            onClick={() => resolve({ action: "archive", sources: selected })}
            className="rounded-lg bg-primary-500 px-3 py-2 text-sm font-medium text-white hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function pickBackupSources(
  reason: ArchiveReason,
  opts: {
    v5Note?: boolean;
    entity?: "company" | "device";
    side?: "sg" | "v5";
  } = {}
): Promise<BackupChoice> {
  return new Promise((resolve) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);
    const done = (choice: BackupChoice) => {
      root.unmount();
      container.remove();
      resolve(choice);
    };
    flushSync(() =>
      root.render(
        <BackupSourcesModal
          reason={reason}
          dark={isDark()}
          v5Note={!!opts.v5Note}
          entity={opts.entity ?? "company"}
          side={opts.side ?? "sg"}
          resolve={done}
        />
      )
    );
  });
}
